import { AxiosError } from 'axios';

import {
  BackOfficeDataProvider,
  BPErrorResponse,
  defaultUrlRenderer
} from 'shared/services/backoffice-data-provider';

import { CampaignDtoConverter } from './CampaignDtoConverter';
import {
  CampaignTranslationRow,
  CampaignTranslationsListPayload,
  RecommendCampaignTranslationParams,
  RecommendCampaignTranslationPayload,
  SaveCampaignTranslationBody
} from './models/campaign-translation.model';

export class CampaignDataProvider extends BackOfficeDataProvider {
  protected dtoConverter = new CampaignDtoConverter();
  protected urlRenderer = defaultUrlRenderer;

  async getCampaignTranslationsList(
    _resource: string,
    params: { campaignId: string }
  ): Promise<{ data: CampaignTranslationsListPayload }> {
    try {
      const {
        data: { payload }
      } = await this.client.get<{ payload: CampaignTranslationsListPayload }>(
        `monetization/campaigns/${params.campaignId}/translations`
      );
      return { data: payload };
    } catch (res) {
      throw (res as AxiosError<BPErrorResponse>).response?.data.error;
    }
  }

  async recommendCampaignTranslation(
    _resource: string,
    params: {
      campaignId: string;
    } & RecommendCampaignTranslationParams
  ): Promise<{ data: RecommendCampaignTranslationPayload }> {
    const { campaignId, target_lang, source_lang, tone } = params;
    try {
      const {
        data: { payload }
      } = await this.client.post<{ payload: RecommendCampaignTranslationPayload }>(
        `monetization/campaigns/${campaignId}/translation/recommend`,
        {
          target_lang,
          ...(source_lang != null && source_lang !== '' ? { source_lang } : {}),
          ...(tone != null && tone !== '' ? { tone } : {})
        }
      );
      return { data: payload };
    } catch (res) {
      throw (res as AxiosError<BPErrorResponse>).response?.data.error;
    }
  }

  async saveCampaignTranslation(
    _resource: string,
    params: {
      campaignId: string;
      languageCode: string;
    } & SaveCampaignTranslationBody
  ): Promise<{ data: CampaignTranslationRow }> {
    const { campaignId, languageCode, ...body } = params;
    const lang = encodeURIComponent(languageCode.trim().toLowerCase());
    try {
      const {
        data: { payload }
      } = await this.client.put<{ payload: CampaignTranslationRow }>(
        `monetization/campaigns/${campaignId}/translations/${lang}`,
        body
      );
      return { data: payload };
    } catch (res) {
      throw (res as AxiosError<BPErrorResponse>).response?.data.error;
    }
  }
}
